// Text-to-speech playback via the Web Speech API (`window.speechSynthesis`).
//
// This is the browser counterpart of the React Native port's expo-speech
// wrapper and exposes the same surface (speak / stop / voices / rate), so the
// shared store consumes it unchanged. Like the RN port — and unlike the Python
// desktop app — nothing is pre-rendered to MP3; each sentence is spoken live.
//
// The Web Speech API has three quirks worth knowing about, all handled here:
//   1. getVoices() is often empty on first call and fills in asynchronously,
//      signalled by the `voiceschanged` event.
//   2. Chrome silently stops speaking after ~15 seconds; a periodic
//      pause()/resume() ping keeps long utterances alive.
//   3. iOS Safari refuses to speak until speech has been started once from a
//      real user gesture — see `primeSpeech()`.
import type { SpeedKey } from './types';

export const SPEED_RATES: Record<SpeedKey, number> = {
  slow: 0.75,
  normal: 1.0,
  fast: 1.35,
};

export const DEFAULT_VOICE = 'default';
export const OFFLINE_KEY = 'offline'; // kept for data/label parity; behaves like default

export interface VoiceOption {
  key: string; // 'default' | voice identifier
  label: string; // display label
  identifier?: string; // undefined => let the engine pick the default English voice
}

/** Mirrors the shape the store expects from the RN `Speech.Voice`. */
export interface WebVoice {
  identifier: string;
  name: string;
  language: string;
}

export function isSupported(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window;
}

let cachedVoices: WebVoice[] | null = null;

function toWebVoice(v: SpeechSynthesisVoice): WebVoice {
  // `name` is used as the identifier: it is what the picker displays, it is
  // unique within a browser, and it is what getVoices() is looked up by when
  // resolving a saved item's voice. voiceURI is not reliably unique in Chrome.
  return { identifier: v.name, name: v.name, language: v.lang };
}

/**
 * Resolve the browser's voice list, waiting for `voiceschanged` when the first
 * synchronous call comes back empty. Resolves with whatever is available after
 * `timeoutMs` rather than hanging.
 */
function getVoicesAsync(timeoutMs = 2000): Promise<SpeechSynthesisVoice[]> {
  return new Promise((resolve) => {
    if (!isSupported()) return resolve([]);

    const immediate = window.speechSynthesis.getVoices();
    if (immediate.length) return resolve(immediate);

    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      window.speechSynthesis.removeEventListener('voiceschanged', onChange);
      clearTimeout(timer);
      resolve(window.speechSynthesis.getVoices());
    };
    const onChange = () => finish();

    window.speechSynthesis.addEventListener('voiceschanged', onChange);
    const timer = setTimeout(finish, timeoutMs);
  });
}

/** English voices reported by the browser, sorted by name (best-effort). */
export async function loadEnglishVoices(): Promise<WebVoice[]> {
  if (cachedVoices) return cachedVoices;
  try {
    const all = await getVoicesAsync();
    const en = all
      .filter((v) => (v.lang || '').toLowerCase().startsWith('en'))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    // Prefer en-US when the browser exposes it, otherwise any English voice.
    const us = en.filter((v) => (v.lang || '').toLowerCase().replace('_', '-') === 'en-us');
    cachedVoices = (us.length ? us : en).map(toWebVoice);
  } catch (e) {
    console.warn('[tts] voice enumeration failed', e);
    cachedVoices = [];
  }
  return cachedVoices;
}

/** Voice identifiers used to cycle distinct voices in persona mode. */
export async function voiceIdentifierPool(): Promise<string[]> {
  const voices = await loadEnglishVoices();
  return voices.map((v) => v.identifier).filter(Boolean);
}

function findVoice(identifier: string | undefined): SpeechSynthesisVoice | null {
  if (!identifier || identifier === DEFAULT_VOICE || identifier === OFFLINE_KEY) return null;
  const all = window.speechSynthesis.getVoices();
  return all.find((v) => v.name === identifier) ?? null;
}

interface SpeakOpts {
  rate?: number;
  voiceId?: string;
  onDone?: () => void;
  onStopped?: () => void;
  onStart?: () => void;
  onError?: (e: unknown) => void;
}

// Chrome's ~15s watchdog workaround.
let keepAlive: ReturnType<typeof setInterval> | null = null;
function startKeepAlive() {
  stopKeepAlive();
  keepAlive = setInterval(() => {
    if (!window.speechSynthesis.speaking) return stopKeepAlive();
    window.speechSynthesis.pause();
    window.speechSynthesis.resume();
  }, 10_000);
}
function stopKeepAlive() {
  if (keepAlive !== null) {
    clearInterval(keepAlive);
    keepAlive = null;
  }
}

// Tracks the utterance we most recently asked for, so late events from a
// cancelled utterance can be ignored.
let currentUtterance: SpeechSynthesisUtterance | null = null;

export function speak(text: string, opts: SpeakOpts = {}): void {
  const clean = text.trim();
  if (!clean) {
    opts.onDone?.();
    return;
  }
  if (!isSupported()) {
    opts.onError?.(new Error('speechSynthesis unavailable'));
    return;
  }

  const synth = window.speechSynthesis;
  // Any pending utterance is ours and is now obsolete.
  synth.cancel();

  const utter = new SpeechSynthesisUtterance(clean);
  utter.lang = 'en-US';
  utter.rate = opts.rate ?? 1.0;
  utter.pitch = 1.0;
  const voice = findVoice(opts.voiceId);
  if (voice) {
    utter.voice = voice;
    utter.lang = voice.lang;
  }

  const isCurrent = () => currentUtterance === utter;

  utter.onstart = () => {
    if (!isCurrent()) return;
    startKeepAlive();
    opts.onStart?.();
  };
  utter.onend = () => {
    if (!isCurrent()) return;
    stopKeepAlive();
    currentUtterance = null;
    opts.onDone?.();
  };
  utter.onerror = (e) => {
    stopKeepAlive();
    if (!isCurrent()) return;
    currentUtterance = null;
    // `interrupted` / `canceled` are what cancel() produces — that is a stop,
    // not a failure, and must not be reported as an error.
    if (e.error === 'interrupted' || e.error === 'canceled') {
      opts.onStopped?.();
    } else {
      console.warn('[tts] utterance error', e.error);
      opts.onError?.(e);
    }
  };

  currentUtterance = utter;
  // Chrome can drop an utterance queued in the same tick as cancel().
  setTimeout(() => {
    if (isCurrent()) synth.speak(utter);
  }, 0);
}

export function stop(): void {
  if (!isSupported()) return;
  stopKeepAlive();
  currentUtterance = null;
  window.speechSynthesis.cancel();
}

export function isSpeakingAsync(): Promise<boolean> {
  return Promise.resolve(isSupported() && window.speechSynthesis.speaking);
}

/**
 * iOS Safari (and some mobile Chrome builds) only allow speech once it has
 * been initiated from a user gesture. Speaking a single silent-ish utterance
 * inside the first real interaction unlocks the engine for the whole session.
 */
let primed = false;
export function primeSpeech(): void {
  if (primed || !isSupported()) return;
  primed = true;
  try {
    const utter = new SpeechSynthesisUtterance('');
    utter.volume = 0;
    window.speechSynthesis.speak(utter);
  } catch {
    // best-effort only
  }
}
